// Night Sky (Oct 27, programming-plan.md Phase E; its own screen, Jared; config/sky.json `nightSky`): tonight's moon,
// the planets worth looking for, the next space station pass you can see, and the meteor showers, all computed in the
// page. astronomy-engine does the moon and planets; satellite.js propagates the station's orbit (Celestrak's elements,
// from the sidecar), and a pass counts when the sky is dark and the station is in sunlight. A line from the cloud
// forecast says whether any of it will show. On the air from just before sunset to sunrise (rules.mjs).
import { registerDisplay } from '../navigation.mjs';
import { escapeHtml } from '../utils/string.mjs';
import FeatureDisplay from './feature-display.mjs';
import { data, fetchedAt } from './snapshot.mjs';
import {
	time, weekday, isToday, dayKey, asOf,
} from './format.mjs';

const { sky: skyConfig, water, clock } = window.RHL;
const cfg = skyConfig.nightSky;
const MINUTE = 60000;
const DAY = 86400000;
const EARTH_KM = 6371;

// the two libraries, loaded once when this module loads (the build copies them to rhl/)
const loadScript = (src, global) => new Promise((resolve) => {
	if (window[global]) {
		resolve(window[global]);
		return;
	}
	const script = document.createElement('script');
	script.src = src;
	script.onload = () => resolve(window[global]);
	script.onerror = () => resolve(null);
	document.head.append(script);
});
let libsReady = false;
let display = null;
Promise.all([loadScript('rhl/astronomy.min.js', 'Astronomy'), loadScript('rhl/satellite.min.js', 'satellite')])
	.then(([A]) => {
		libsReady = !!A;
		// the screen checked for data before the libraries came; check again now rather than at the next refresh
		if (libsReady && display?.weatherParameters) display.getData(display.weatherParameters, true);
	});

const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
const compass8 = (deg) => COMPASS[Math.round((((deg % 360) + 360) % 360) / 45) % 8];
const when = (t, now) => `${isToday(t, now) ? '' : `${weekday(t)} `}${time(t)}`;

const observer = () => new window.Astronomy.Observer(water.point.lat, water.point.lon, 30);
const horizon = (body, date, obs) => {
	const A = window.Astronomy;
	const eq = A.Equator(body, date, obs, true, true);
	return A.Horizon(date, obs, eq.ra, eq.dec, 'normal');
};

const moonLine = (now) => {
	const A = window.Astronomy;
	const obs = observer();
	const percent = Math.round(A.Illumination(A.Body.Moon, now).phase_fraction * 100);
	const angle = A.MoonPhase(now);
	const name = [[10, 'New moon'], [80, 'Waxing crescent'], [100, 'First quarter'], [170, 'Waxing gibbous'], [190, 'Full moon'],
		[260, 'Waning gibbous'], [280, 'Last quarter'], [350, 'Waning crescent'], [361, 'New moon']].find(([max]) => angle < max)[1];
	const up = horizon(A.Body.Moon, now, obs).altitude > 0;
	const next = A.SearchRiseSet(A.Body.Moon, obs, up ? -1 : +1, now, 1)?.date;
	return {
		label: 'Moon', value: `${name}, ${percent}%`, note: next ? `${up ? 'sets' : 'rises'} ${when(next, now)}` : '',
	};
};

// each planet's best moment tonight: its highest point while the sky is dark, sampled every 15 minutes to sunrise
const planetLines = (now) => {
	const A = window.Astronomy;
	const obs = observer();
	const samples = [];
	for (let m = 0; m <= 16 * 60; m += 15) {
		const t = new Date(now.getTime() + m * MINUTE);
		if (horizon(A.Body.Sun, t, obs).altitude < cfg.darkSunAltitude) samples.push(t);
	}
	return cfg.planets.map((name) => {
		let best = null;
		let first = null;
		samples.forEach((t) => {
			const h = horizon(A.Body[name], t, obs);
			if (h.altitude < cfg.minAltitude) return;
			first ??= t;
			if (!best || h.altitude > best.altitude) best = { t, altitude: h.altitude, azimuth: h.azimuth };
		});
		if (!best) return null;
		const { mag } = A.Illumination(A.Body[name], now);
		// how high at its best: high from 50 degrees, low under 25, else just where to look
		let height = '';
		if (best.altitude >= 50) height = ', high';
		else if (best.altitude < 25) height = ', low';
		let note = mag < -1.5 ? 'very bright' : '';
		if (first > now) note = `up by ${time(first)}`;
		return {
			label: name, value: `${compass8(best.azimuth)}${height} at ${time(best.t)}`, note, mag,
		};
	}).filter(Boolean).sort((a, b) => a.mag - b.mag).slice(0, 3);
};

// the next pass of the space station you can see: above minPassElevation, the sky dark, the station sunlit
let passCache = { key: null, pass: null };
const issPass = (now) => {
	const elements = data('iss');
	const sat = window.satellite;
	if (!elements || !sat) return null;
	const key = `${elements.line1}|${Math.floor(now / (10 * MINUTE))}`;
	if (passCache.key === key) return passCache.pass;
	const A = window.Astronomy;
	const obs = observer();
	const rec = sat.twoline2satrec(elements.line1, elements.line2);
	const site = { longitude: sat.degreesToRadians(water.point.lon), latitude: sat.degreesToRadians(water.point.lat), height: 0.03 };
	let pass = null;
	let found = null;
	for (let s = 0; s < (cfg.passDays * DAY) / 1000 && !found; s += 20) {
		const t = new Date(now.getTime() + s * 1000);
		const pv = sat.propagate(rec, t);
		if (pv.position) {
			const look = sat.ecfToLookAngles(site, sat.eciToEcf(pv.position, sat.gstime(t)));
			const elevation = (look.elevation * 180) / Math.PI;
			if (elevation > cfg.minPassElevation) {
				// in sunlight: on the sun's side of the Earth, or outside its shadow cylinder
				const sun = A.GeoVector(A.Body.Sun, t, true);
				const len = Math.hypot(sun.x, sun.y, sun.z);
				const u = [sun.x / len, sun.y / len, sun.z / len];
				const r = [pv.position.x, pv.position.y, pv.position.z];
				const along = r[0] * u[0] + r[1] * u[1] + r[2] * u[2];
				const lit = along > 0 || Math.hypot(r[0] - along * u[0], r[1] - along * u[1], r[2] - along * u[2]) > EARTH_KM;
				const dark = horizon(A.Body.Sun, t, obs).altitude < -6;
				const azimuth = (look.azimuth * 180) / Math.PI;
				pass ??= {
					start: t, from: azimuth, max: 0, seen: false,
				};
				pass.end = t;
				pass.to = azimuth;
				pass.max = Math.max(pass.max, elevation);
				if (lit && dark) pass.seen = true;
			} else if (pass) {
				if (pass.seen) found = pass;
				pass = null;
			}
		}
	}
	passCache = { key, pass: found };
	return found;
};
const issLine = (now) => {
	const pass = issPass(now);
	if (!pass) return null;
	const minutes = Math.max(1, Math.round((pass.end - pass.start) / MINUTE));
	return {
		label: 'Space station',
		value: `${when(pass.start, now)}, ${minutes} min`,
		note: `${compass8(pass.from)} to ${compass8(pass.to)}${pass.max >= 40 ? ', high' : ''}`,
	};
};

// a shower going on now, else the next peak within ten days
const meteorLine = (now) => {
	const md = dayKey(now).slice(5);
	const inRange = (m) => (m.from <= m.to ? md >= m.from && md <= m.to : md >= m.from || md <= m.to);
	const peakText = (m) => new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' })
		.format(new Date(`${dayKey(now).slice(0, 4)}-${m.peak}T12:00:00Z`));
	const active = cfg.meteors.find(inRange);
	if (active) return { label: 'Meteors', value: `${active.name} active`, note: `peak ${peakText(active)}` };
	const soon = cfg.meteors.find((m) => {
		const days = (Date.parse(`${dayKey(now).slice(0, 4)}-${m.peak}T12:00:00Z`) - Date.parse(`${dayKey(now)}T12:00:00Z`)) / DAY;
		return days > 0 && days <= 10;
	});
	return soon ? { label: 'Meteors', value: `${soon.name} soon`, note: `peak ${peakText(soon)}` } : null;
};

// clouds tonight: the most of low, mid and high cover at cloudHour
const cloudLine = (now) => {
	const sky = data('sky');
	if (!sky?.times) return '';
	const target = sky.times.findIndex((t) => {
		const d = new Date(t);
		const hour = Number(new Intl.DateTimeFormat('en-US', { timeZone: clock.timeZone, hour: 'numeric', hourCycle: 'h23' }).format(d));
		return d > now - 2 * 3600000 && hour === cfg.cloudHour;
	});
	if (target < 0) return '';
	const cover = Math.max(sky.low[target], sky.mid[target], sky.high[target]);
	if (cover >= cfg.cloudyPercent) return `Clouds tonight ${cover}%: stars unlikely`;
	if (cover <= cfg.clearPercent) return 'Clear tonight';
	return `Clouds tonight ${cover}%`;
};

class NightSky extends FeatureDisplay {
	constructor(navId, elemId) {
		super(navId, elemId, "Tonight's Sky");
	}

	// eslint-disable-next-line class-methods-use-this
	hasData() {
		return libsReady;
	}

	// eslint-disable-next-line class-methods-use-this
	buildPages(now) {
		if (!libsReady) return [];
		const rows = [moonLine(now), ...planetLines(now), issLine(now), meteorLine(now)].filter(Boolean);
		const clouds = cloudLine(now);
		return [{
			seconds: 14,
			title: ["Tonight's", 'Sky'],
			html: `<div class="hdr">Russian Hill, after dark${clouds ? ` &middot; ${escapeHtml(clouds)}` : ''}</div>
				<div class="list night-sky">${rows.map((r) => `<div class="row"><div>${escapeHtml(r.label)}</div><div class="v">${escapeHtml(r.value)}</div><div class="dim">${escapeHtml(r.note)}</div></div>`).join('')}</div>
				<div class="asof">Station orbit: Celestrak${fetchedAt('iss') ? ` &middot; ${asOf(fetchedAt('iss'), now)}` : ''}</div>`,
		}];
	}
}

display = new NightSky(34, 'night-sky');
display.makeLoopScreen();
registerDisplay(display);
